import React, {useEffect, useState, useContext} from "react";
import "./TopicDescriptionOverview.css";
import {FormattedMessage, useIntl} from "react-intl";
import API, {Resolution} from "../../logic/API";
import {TopicDescription, TopicDescriptionInfo} from "../../logic/APITypes";
import {ScreenSize} from "../utility/ScreenSize";
import {
  IconButton,
  ModalContext,
  TableColumn,
  Icon,
  AuxiliaryToolTip,
  TextInput,
} from "@spg/component_library";
import {SnackbarContext} from "../../reusables/SnackbarProvider";
import {TopicDescriptionTest} from "../../reusables/TopicDescriptionTest";
import {TopicDescriptionCreation} from "./singleTopicDescription/TopicDescriptionCreation";
import edit from "../../images/edit.svg";
import trashIcon from "../../images/trash.svg";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {NavigationManagement} from "../LandingPage";
import keyIcon from "../../images/key.svg";
import plusIcon from "../../images/plus.svg";
import infoIcon from "../../images/info.svg";
import closeIcon from "../../images/closeIcon.svg";
import {Table} from "../../reusables/Table";
import {ToggleStatusButton} from "../../reusables/ToggleStatusButton";
import {generateTopicDescriptionText} from "../../logic/IntlTexts";
import {doesListSatisfyFilter} from "../../logic/ListFilter";
import {TopicContentViewer} from "./TopicContentViewer/TopicContentViewer";
import {keycloak} from "../utility/KeycloakProvider";

/**
 * A page with a tabular overview of all topics or the detail page of a selected topic
 */
function TopicDescriptionOverview(props: {
  navigationManagement: NavigationManagement;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const addSnackbar = useContext(SnackbarContext).addSnackbar;

  const urlTopicId = location.pathname.startsWith("/topics/overview/")
    ? location.pathname.split("/")[3]
    : undefined;
  const [topicDescriptions, setTopicDescriptions] = useState<
    Resolution<Array<TopicDescriptionInfo>>
  >({
    success: false,
    unresolved: true,
  });
  const [search, setSearch] = useState("");
  const [editingTopic, setEditingTopic] = useState<
    | {
        alternative: "view" | "edit";
        topic: Resolution<TopicDescription>;
        id: string;
      }
    | {alternative: "create"}
  >();
  const [freshTopicDescription, setFreshTopicDescription] =
    useState<TopicDescription>();

  const columns: TableColumn[] = [
    {
      key: "name",
      label: <FormattedMessage id="topicDescriptionOverview.name" />,
      sortable: true,
      columnCount: {
        "Big Desktop": 3,
        Desktop: 3,
        Tablet: 2,
        Mobile: 3,
      },
    },
    {
      key: "description",
      label: <FormattedMessage id="topicDescriptionOverview.description" />,
      collapsibleMobile: true,
      columnCount: {
        "Big Desktop": 4,
        Desktop: 4,
        Tablet: 4,
      },
    },
    {
      key: "owner_name",
      label: <FormattedMessage id="topicDescriptionOverview.owner_name" />,
      sortable: true,
      collapsibleMobile: true,
      collapsibleTablet: true,
      columnCount: {
        "Big Desktop": 2,
        Desktop: 2,
      },
    },
    {
      key: "health",
      label: <FormattedMessage id="topicDescriptionOverview.health" />,
      sortable: true,
      collapsibleMobile: true,
      columnCount: {
        "Big Desktop": 2,
        Desktop: 2,
      },
    },
    {
      key: "edit",
      label: <FormattedMessage id="topicDescriptionOverview.edit" />,
      srOnlyLabel: true,
      sortable: false,
      columnCount: {
        "Big Desktop": 1,
        Desktop: 1,
        Tablet: 1,
      },
    },
  ];

  const getTopicDescriptions = () => {
    API.getTopicDescriptions().then((response) => {
      setTopicDescriptions(response);
    });
  };

  const fetchAndSetEditingTopic = (
    alternative: "view" | "edit",
    description: TopicDescriptionInfo,
    useNavigation: boolean,
  ) => {
    if (useNavigation) {
      localStorage.setItem("topicDescriptionCreationOriginPage", "topics");
      navigate("/topics/overview/" + description.id);
    }
    setFreshTopicDescription(undefined);
    if (freshTopicDescription && alternative === "view") {
      setEditingTopic({
        alternative,
        id: freshTopicDescription.id,
        topic: {success: true, payload: freshTopicDescription},
      });
    } else {
      setEditingTopic({
        alternative,
        id: description.id,
        topic: {success: false, unresolved: true},
      });
      API.getTopicDescription(description.id).then((response) => {
        if (response.success)
          setEditingTopic({
            alternative,
            id: description.id,
            topic: response,
          });
        else {
          addSnackbar({
            variant: "close",
            text: intl.formatMessage({
              id: "topicDescriptionOverview.snackbar.view.error.text",
            }),
            hasCloseButton: true,
          });
          if (
            localStorage.getItem("topicDescriptionCreationOriginPage") ===
            "home"
          )
            navigate("/home");
          else {
            setEditingTopic(undefined);
            navigate("/topics");
          }
        }
      });
    }
  };

  const updateTopicStatus = (content: TopicDescriptionInfo) => {
    if (topicDescriptions.payload) {
      const index = topicDescriptions.payload?.findIndex(
        (element) => element.id === content.id,
      );

      if (index !== -1) {
        const updatedTopics = {...topicDescriptions};
        if (updatedTopics.payload) updatedTopics.payload[index] = content;
        setTopicDescriptions(updatedTopics);
      }
    }
  };

  const deleteTopic = (id: string) => {
    if (topicDescriptions.payload) {
      const index = topicDescriptions.payload?.findIndex(
        (element) => element.id === id,
      );
      if (index !== -1) {
        const updatedTopics = {...topicDescriptions};
        if (updatedTopics.payload) updatedTopics.payload.splice(index, 1);
        setTopicDescriptions(updatedTopics);
      }
    }
  };

  const rows = !topicDescriptions.payload
    ? []
    : topicDescriptions.payload
        .filter((description) =>
          doesListSatisfyFilter(
            [
              description.name,
              description.owner_name,
              description.id,
              generateTopicDescriptionText(intl, description, "METHOD"),
              generateTopicDescriptionText(intl, description, "HEALTH"),
              description.description,
            ],
            search,
          ),
        )
        .map((description, index) => ({
          id: description.id,
          cells: {
            id: description.id,
            name: {
              content: description.name,
              cellClasses: ["ACTIVE_NO_ERROR", "INACTIVE"].includes(
                description.health,
              )
                ? undefined
                : "healthProblem" +
                  (description.visibility === "PRIVATE"
                    ? " multipleIcons"
                    : ""),
              hasToolTip: true,
              link: {
                to: "/topics/overview/" + description.id,
                onClick: () => {
                  localStorage.setItem(
                    "topicDescriptionCreationOriginPage",
                    "topics",
                  );
                },
              },
              additionalIcon:
                description.visibility === "PRIVATE" ||
                !["INACTIVE", "ACTIVE_NO_ERROR"].includes(
                  description.health,
                ) ? (
                  <>
                    {description.visibility === "PRIVATE" && (
                      <div id={`privateIcon_${description.id}`}>
                        <Icon
                          href={keyIcon + "#globalG"}
                          title={intl.formatMessage({
                            id: "topicDescriptionOverview.private",
                          })}
                        />
                      </div>
                    )}
                    {!["INACTIVE", "ACTIVE_NO_ERROR"].includes(
                      description.health,
                    ) && (
                      <AuxiliaryToolTip
                        toolTip={intl.formatMessage({
                          id: `topicDescriptionOverview.info.${
                            description.edit_authorized ? "AUTHORIZED." : ""
                          }${description.transfer_type}.${
                            description.direction
                          }.${description.health}`,
                        })}
                        idBase={"topicDescriptionOverviewNameInfoIcon" + index}
                        tooltipPosition={"bottom"}
                        toggleMessage={intl.formatMessage({
                          id: "topicDescriptionOverview.info.toggle",
                        })}
                      >
                        <Icon
                          href={infoIcon + "#globalG"}
                          title={intl.formatMessage({
                            id: "topicDescriptionOverview.info",
                          })}
                          strokeColor="#b32424"
                        />
                      </AuxiliaryToolTip>
                    )}
                  </>
                ) : undefined,
            },
            description: {
              content: description.description,
              cellClasses: ["ACTIVE_NO_ERROR", "INACTIVE"].includes(
                description.health,
              )
                ? undefined
                : "healthProblem",
              hasToolTip: true,
            },
            owner_name: {
              content: description.owner_name,
              cellClasses: ["ACTIVE_NO_ERROR", "INACTIVE"].includes(
                description.health,
              )
                ? undefined
                : "healthProblem",
              hasToolTip: true,
            },
            health: {
              content: generateTopicDescriptionText(
                intl,
                description,
                "HEALTH",
              ),
              cellClasses: ["ACTIVE_NO_ERROR", "INACTIVE"].includes(
                description.health,
              )
                ? undefined
                : "healthProblem",
            },
            edit: {
              sortingKey: "",
              value: description.edit_authorized ? (
                <IconButton
                  idBase={`edit_${description.id}_`}
                  onClick={() =>
                    fetchAndSetEditingTopic("edit", description, true)
                  }
                  buttonSize={{height: 48, width: 48}}
                  iconProps={{
                    href: edit + "#globalG",
                    strokeColor: "#FF3D00",
                    title: intl.formatMessage({
                      id: "topicDescriptionOverview.edit",
                    }),
                  }}
                />
              ) : (
                <></>
              ),
            },
          },
          expandableContent: (
            <ExpandableContent
              index={index}
              content={description}
              onUpdateStatus={updateTopicStatus}
              onDelete={deleteTopic}
            />
          ),
        }));

  useEffect(() => {
    getTopicDescriptions();
  }, []);

  useEffect(() => {
    if (location.pathname === "/topics/create") {
      setEditingTopic({alternative: "create"});
      return;
    }
    if (!urlTopicId || !topicDescriptions.payload) {
      setEditingTopic(undefined);
      return;
    }
    const topic = topicDescriptions.payload.find(
      (desc) => desc.id === urlTopicId,
    );
    if (
      topic &&
      (!editingTopic ||
        (editingTopic && "id" in editingTopic && topic.id !== editingTopic?.id))
    )
      fetchAndSetEditingTopic("view", topic, false);
  }, [location.pathname, topicDescriptions.payload]);

  useEffect(() => {
    if (props.navigationManagement.isTryingToNavigate.length === 0) return;
    if (!topicDescriptions.payload || !editingTopic)
      props.navigationManagement.allowNavigation();
  }, [props.navigationManagement.isTryingToNavigate]);

  // Render the contentViewer-page instead of the table
  if (location.pathname.startsWith("/topics/datasets/")) {
    return <TopicContentViewer topicList={topicDescriptions.payload} />;
  }

  // Don't show table while fetching the editing topic after entering from another page
  if (
    (topicDescriptions.unresolved && (editingTopic || urlTopicId)) ||
    (!editingTopic &&
      urlTopicId &&
      (topicDescriptions.payload ?? []).some((desc) => desc.id === urlTopicId))
  )
    return null;

  // Render the detail- or edit-page instead of the table
  if (topicDescriptions.payload && editingTopic) {
    if (editingTopic.alternative == "create")
      return (
        <TopicDescriptionCreation
          alternative={"create"}
          navigationManagement={props.navigationManagement}
          onSuccessfulCreation={(topic: TopicDescription) => {
            const newTopic = {
              ...topic,
              is_owner: true,
              edit_authorized: true,
              owner_name: keycloak.idTokenParsed
                ? `${keycloak.idTokenParsed["given_name"]} ${keycloak.idTokenParsed["family_name"]}`
                : "",
              direction: topic.transfer.direction,
              transfer_type: topic.transfer.transfer_type,
            };
            setTopicDescriptions((oldTopics) => ({
              ...oldTopics,
              payload: [...(oldTopics.payload || []), newTopic],
            }));
            setEditingTopic(undefined);
            setFreshTopicDescription(newTopic);
            navigate("/topics/overview/" + topic.id);
          }}
          onCancel={() => {
            navigate("/topics/overview");
            setEditingTopic(undefined);
          }}
        />
      );
    const selectedTopic = topicDescriptions.payload.find(
      (t) => t.id === editingTopic.id,
    );
    if (selectedTopic)
      return (
        <TopicDescriptionCreation
          navigationManagement={props.navigationManagement}
          alternative={editingTopic.alternative}
          viewEditInfo={{
            onSuccessfulEdit: (topic) => {
              if (topicDescriptions.payload) {
                const index = topicDescriptions.payload?.findIndex(
                  (element) => element.id === topic.id,
                );
                if (index !== -1) {
                  const updatedTopics = {...topicDescriptions};
                  if (updatedTopics.payload)
                    updatedTopics.payload[index] = {
                      ...topic,
                      edit_authorized:
                        updatedTopics.payload[index].edit_authorized,
                      is_owner: updatedTopics.payload[index].is_owner,
                      owner_name: updatedTopics.payload[index].owner_name,
                      direction: topic.transfer.direction,
                      transfer_type: topic.transfer.transfer_type,
                    };
                  setTopicDescriptions(updatedTopics);
                }
                setEditingTopic({
                  alternative: "view",
                  id: topic.id,
                  topic: {...editingTopic.topic, payload: topic},
                });
              }
            },
            onBack: () => {
              navigate("/topics/overview");
              setEditingTopic(undefined);
            },
            onTransferred: (newOwnerName) => {
              navigate("/topics/overview");
              if (topicDescriptions.payload) {
                const index = topicDescriptions.payload?.findIndex(
                  (element) => element.id === editingTopic.id,
                );
                if (index !== -1) {
                  const updatedTopics = {...topicDescriptions};
                  if (!updatedTopics.payload) return;
                  if (updatedTopics.payload[index].visibility === "PUBLIC")
                    updatedTopics.payload[index] = {
                      ...updatedTopics.payload[index],
                      edit_authorized:
                        keycloak.hasResourceRole("DM Administration"),
                      is_owner: false,
                      owner_name: newOwnerName,
                    };
                  else delete updatedTopics.payload[index];
                  setTopicDescriptions(updatedTopics);
                }
              }
              setEditingTopic(undefined);
            },
            initialTopic: editingTopic.topic,
            initialInfo: selectedTopic,
          }}
        />
      );
  }

  return (
    <div className="landingPageGrid topicDescriptionOverview">
      <h1 id="topicDescriptionOverviewHeading">
        <FormattedMessage id="topicDescriptionOverview.title" />
      </h1>
      <Link
        to={window.location.origin + "/datamarket/topics/create"}
        id="createNewTopicLink"
        className="topicDescriptionOverviewNew"
        onClick={() => {
          localStorage.setItem("topicDescriptionCreationOriginPage", "topics");
        }}
      >
        <Icon
          href={plusIcon + "#globalG"}
          title={""}
          iconSize={{width: 36, height: 36}}
        />
        <span>
          <FormattedMessage id="topicDescriptionOverview.new" />
        </span>
      </Link>
      {!topicDescriptions.unresolved &&
      (!topicDescriptions.payload || topicDescriptions.payload.length === 0) ? (
        <div className="topicDescriptionOverviewBlankText">
          <FormattedMessage
            id={
              "topicDescriptionOverview." +
              (topicDescriptions.success ? "blank" : "failed")
            }
          />
        </div>
      ) : (
        <>
          <TextInput
            label={
              search.length === 0
                ? intl.formatMessage({
                    id: "topicDescriptionOverview.search.label.blank",
                  })
                : intl.formatMessage(
                    {
                      id: "topicDescriptionOverview.search.label",
                    },
                    {
                      hits: rows.length,
                    },
                  )
            }
            placeholder={intl.formatMessage({
              id: "topicDescriptionOverview.search.placeholder",
            })}
            idBase={"topicDescriptionOverviewSearch"}
            isDisabled={topicDescriptions.unresolved}
            value={search}
            trailingIconButton={
              search.length > 0
                ? {
                    idBase: "topicDescriptionOverviewSearchDelete",
                    onClick: () => setSearch(""),
                    iconProps: {
                      href: closeIcon + "#globalG",
                      strokeColor: "var(--semIconbtnColorIconDefault)",
                      title: intl.formatMessage({
                        id: "topicDescriptionOverview.search.clear",
                      }),
                    },
                    hoverColor: "var(--semTableColorIconHover)",
                  }
                : undefined
            }
            setValue={(search) => {
              setSearch(search);
            }}
          />
          {rows.length > 0 ? (
            <Table
              id={"topicTable"}
              data={rows}
              columns={columns}
              pageSize={12}
              ariaLabelledBy={"topicDescriptionOverviewHeading"}
              isLoading={topicDescriptions.unresolved}
            />
          ) : (
            <div className="topicDescriptionOverviewBlankText">
              <FormattedMessage id={"topicDescriptionOverview.search.noHits"} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export {TopicDescriptionOverview};

type ExpandableContentProps = {
  /** An index to avoid id collisions */
  index: number;
  /** The relevant content to display */
  content: TopicDescriptionInfo;
  /** Function triggered when the status of a topic is updated */
  onUpdateStatus: (content: TopicDescriptionInfo) => void;
  /** Function triggered when a topic is deleted */
  onDelete: (id: string) => void;
};

function ExpandableContent(props: ExpandableContentProps) {
  const screenSize = useContext(ScreenSize);
  const addSnackbar = useContext(SnackbarContext).addSnackbar;
  const modalContext = useContext(ModalContext);
  const intl = useIntl();
  const [interactionStatus, setInteractionStatus] = useState<
    "IDLE" | "PENDING"
  >("IDLE");

  const deleteTopic = () => {
    setInteractionStatus("PENDING");
    API.deleteTopic(props.content.id).then((resolution) => {
      if (resolution.success) {
        props.onDelete(props.content.id);
        addSnackbar({
          variant: "timeout",
          text: intl.formatMessage({id: "topicDescriptionDeletion.success"}),
          timeout: 7000,
        });
      } else {
        addSnackbar({
          variant: "close",
          text: intl.formatMessage({id: "topicDescriptionDeletion.error"}),
          hasCloseButton: true,
        });
      }
      setInteractionStatus("IDLE");
    });
  };

  return (
    <div className="topicExpandContainer">
      <div className="topicExpandColumn">
        <div className="topicExpandDescription">
          <div className="topicExpandHeader">
            <FormattedMessage id="topicDescriptionCreation.inputLabel.description" />
          </div>
          {props.content.description}
        </div>
        <div className="topicExpandDescription">
          <div className="topicExpandHeader">
            <FormattedMessage id="topicDescriptionOverview.owner_name" />
          </div>
          {props.content.owner_name}
        </div>
        <div className="topicExpandDescription">
          <div className="topicExpandHeader">
            <FormattedMessage id="topicDescriptionCreation.h2.transfer" />
          </div>
          {generateTopicDescriptionText(intl, props.content, "METHOD")}
        </div>
      </div>
      {(screenSize.category === "Desktop" ||
        screenSize.category === "Big Desktop") && (
        <div className="topicExpandDivider"></div>
      )}
      <div className="topicExpandColumn">
        {props.content.edit_authorized && (
          <div className="topicExpandDescription">
            <div className="topicExpandHeader">
              <FormattedMessage id="topicDescriptionOverview.health" />
            </div>
            <div className="topicExpandStatus">
              {generateTopicDescriptionText(intl, props.content, "HEALTH")}
              <ToggleStatusButton
                topic={{
                  ...props.content,
                  isToggling: interactionStatus === "PENDING",
                }}
                setTopic={(newTopic) => {
                  if (newTopic.health !== props.content.health)
                    props.onUpdateStatus(newTopic);
                  setInteractionStatus(
                    newTopic.isToggling ? "PENDING" : "IDLE",
                  );
                }}
              />
            </div>
          </div>
        )}
        <div
          className={
            "topicExpandInteractionDeleteContainer" +
            (props.content.direction === "PUSH" ? " push" : "")
          }
        >
          {props.content.direction === "PULL" && (
            <TopicDescriptionTest index={props.index} id={props.content.id} />
          )}
          {props.content.edit_authorized && (
            <IconButton
              idBase={`delete_${props.content.id}_`}
              onClick={() => {
                modalContext.showModal({
                  idBase: "topicDescriptionCreationCancel",
                  headline: intl.formatMessage({
                    id: "topicDescriptionDeletion.modal.headline",
                  }),
                  body: (
                    <div className="modalText">
                      <FormattedMessage
                        id="topicDescriptionDeletion.modal.text"
                        values={{
                          br: () => <br />,
                        }}
                      />
                    </div>
                  ),
                  primaryButton: {
                    name: intl.formatMessage({
                      id: "topicDescriptionDeletion.modal.delete",
                    }),
                    onClick: () => deleteTopic(),
                  },
                  secondaryButton: {
                    name: intl.formatMessage({
                      id: "topicDescriptionDeletion.modal.cancel",
                    }),
                    onClick: () => undefined,
                  },
                });
              }}
              hoverColor="var(--semTableColorIconHover)"
              iconProps={{
                href: trashIcon + "#globalG",
                strokeColor: "var(--semIconbtnColorIconDefault)",
                title: intl.formatMessage(
                  {
                    id: "topicDescriptionDeletion.a11y",
                  },
                  {index: props.content.name},
                ),
              }}
              disabled={interactionStatus === "PENDING"}
            />
          )}
        </div>
      </div>
    </div>
  );
}
